import { Component, ElementRef, Input, OnDestroy, OnInit, Renderer2, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription, forkJoin } from 'rxjs';
import { collapseMenuHubs } from '../../_services/menu-hub';
import { Popover } from 'primeng/popover';
import { ConfigService } from '../../_services/config.service';
import { PortalService } from '../../portale/_services/portal.service';
import { SidebarService } from '../app-sidebar-minimizer/sidebar.service';
import { UserAccessibleRoutesService } from '../../dashboard/_services/user-accessible-routes.service';
import { MenuEditService } from '../../_services/menu-edit.service';
import { MenuLayoutService } from '../../_services/menu-layout.service';
import { conPadriInTendina, senzaVociNascoste } from '../../_services/menu-organization';

/** Normalizza un url di menu per navigateByUrl: alcune pagine hanno lo slash finale
 *  nel DB (es. 'hr/') che il matcher del router NON tollera (NG04002; il vecchio
 *  navigate([...]) lo scartava silenziosamente). Rimuove gli slash finali e i
 *  doppioni iniziali preservando l'eventuale query string. */
export function menuRouterUrl(url: any): string {
	return (
		'/' +
		String(url)
			.replace(/^\/+/, '')
			.replace(/\/+(\?|$)/, '$1')
	);
}

/** True se l'URL del menu corrisponde alla rotta corrente (anche per sotto-rotte di dettaglio).
 *  Normalizza gli slash: le voci di sottomenu hanno pg_url con slash iniziale (es. "/automezzi/costi"),
 *  i top-level no (es. "report"), e alcune hanno slash finale. */
export function isMenuUrlActive(router: Router, url: any): boolean {
	if (!url || typeof url !== 'string' || url === 'null') {
		return false;
	}
	const norm = (s: string) => '/' + s.split('?')[0].split('#')[0].replace(/^\/+/, '').replace(/\/+$/, '');
	const target = norm(url);
	if (target === '/') {
		return false;
	}
	const current = norm(router.url || '');
	return current === target || current.startsWith(target + '/');
}

@Component({
	standalone: false,
	selector: 'app-sidebar-nav',
	changeDetection: ChangeDetectionStrategy.Eager,
	template: ` @if (menuEdit.attivo() && menuLayout.layout === 'left') {
		<!-- matita dell'header con il menu a sinistra: il menu si modifica qui -->
		<app-menu-editor [verticale]="true"></app-menu-editor>
	} @else if (navLoaded) {
		<nav class="sidebar-nav">
			<ul class="nav" style="cursor: pointer; flex-direction: column">
				@for (navitem of menuItems; track navitem) {
					@if (isDivider(navitem)) {
						<li class="nav-divider"></li>
					}
					@if (isTitle(navitem)) {
						<app-sidebar-nav-title [title]="navitem"></app-sidebar-nav-title>
					}
					@if (!isDivider(navitem) && !isTitle(navitem)) {
						<app-sidebar-nav-item [item]="navitem" routerLinkActive="active"></app-sidebar-nav-item>
					}
				}
			</ul>
		</nav>
	}`,
})
export class AppSidebarNavComponent implements OnInit, OnDestroy {
	currentUser: any = null;
	loginAttivo = false;
	menuItems: any[] = [];
	num_lette: number = 0;
	navLoaded = false;
	private menuChangedSub?: Subscription;
	// public navigation = navigation;

	public isDivider(item: any) {
		return item.divider ? true : false;
	}

	public isTitle(item: any) {
		return item.title ? true : false;
	}

	constructor(
		private portalservice: PortalService,
		private configService: ConfigService,
		private router: Router,
		private userAccessibleRoutes: UserAccessibleRoutesService,
		public menuEdit: MenuEditService,
		public menuLayout: MenuLayoutService,
	) {}

	ngOnInit() {
		this.setLang(this.configService.language);
		this.currentUser = this.configService.getLocalStorageObjectJson('currentUser');
		if (this.currentUser != null) {
			//MI INIZIO 16-06-2023
			if (this.currentUser.usr_primoaccesso === 1) {
				this.router.navigate(['/portale/modpassword', this.currentUser.usr_id]);
				return;
			}
			//MI FINE 16-06-2023

			//const portale = this.configService.getLocalStorageObjectJson('portale');
			this.loginAttivo = true;
			this.loadMenu();
			// l'organizzazione menu dell'azienda attiva e' stata modificata: ricarica
			this.menuChangedSub = this.portalservice.menuChanged$.subscribe(() => this.loadMenu());
		}
	}

	ngOnDestroy() {
		this.menuChangedSub?.unsubscribe();
	}

	private loadMenu() {
		const prt = this.configService.getPortalID();
		const cmp = this.configService.getCompany_id();
		forkJoin([this.portalservice.getMenuItems(prt, cmp), this.portalservice.getMenuHubs(prt, cmp)]).subscribe(([menuItems, hubs]) => {
			// sezioni a schede: la voce padre apre il cruscotto, senza sottomenu
			// una voce con pagina e sottomenu apre la tendina: la sua pagina e' la prima voce
			this.menuItems = conPadriInTendina(senzaVociNascoste(collapseMenuHubs(menuItems, hubs, cmp)));
			// Popola la cache delle route accessibili per il filtro permessi del catalog widget
			// (menu completo: le pagine delle sezioni a schede restano accessibili)
			this.userAccessibleRoutes.loadFromMenu(menuItems);

			const keypage = localStorage.getItem('keypage');
			// Un utente senza voci di menu - per esempio appena registrato, con ruolo guest -
			// arriva qui con la lista vuota: senza il controllo si legge undefined.url e
			// l'errore interrompe il caricamento della pagina dopo l'accesso
			let primaPag: string = this.menuItems.length > 0 ? this.menuItems[0].url : '';
			if (keypage != null && keypage !== undefined) {
				for (let i = 0; i < this.menuItems.length; i++) {
					if (this.menuItems[i].url === keypage) {
						primaPag = keypage;
						localStorage.removeItem('keypage');
						break;
					}
				}
			}
			this.navLoaded = true;
			//this.router.navigate([primaPag]);
		});
	}

	setNotNum(event: any) {
		this.configService.totNot(event);
	}
	setLang(lang: string) {
		//this.translate.use();
	}
}

@Component({
	standalone: false,
	selector: 'app-sidebar-nav-item',
	changeDetection: ChangeDetectionStrategy.Eager,
	template: `
		@if (!isDropdown()) {
			<li [ngClass]="hasClass() ? 'nav-item w-full ' + item.class : 'nav-item w-full'" pTooltip="{{ item.pg_title | translate }}" tooltipPosition="right" [tooltipDisabled]="!minimized || disableTooltip">
				<app-sidebar-nav-link [link]="item"></app-sidebar-nav-link>
			</li>
		} @else {
			<li [ngClass]="hasClass() ? 'nav-item nav-dropdown ' + item.class : 'nav-item nav-dropdown'" [class.open]="isActive()" routerLinkActive="open" appNavDropdown>
				<app-sidebar-nav-dropdown [link]="item"></app-sidebar-nav-dropdown>
			</li>
		}
	`,
})
export class AppSidebarNavItemComponent {
	@Input() item: any;
	@Input() disableTooltip = false;
	minimized = false;

	constructor(
		private router: Router,
		private sidebarService: SidebarService,
	) {}

	ngOnInit() {
		this.sidebarService.minimized$.subscribe((min) => (this.minimized = min));
	}

	public hasClass() {
		return this.item.class ? true : false;
	}

	public isDropdown() {
		// il menu organizzato per azienda azzera submenu a [] sulle foglie: un array
		// vuoto non e' un dropdown (altrimenti la voce perde w-full e tooltip)
		return Array.isArray(this.item.submenu) ? this.item.submenu.length > 0 : !!this.item.submenu;
	}

	public thisUrl() {
		return this.item.url;
	}

	public isActive() {
		if (this.item.url === undefined || this.item.url === null || this.item.url === '' || this.item.url === 'null') {
			return false;
		}
		return this.router.isActive(this.thisUrl(), false);
	}
}

// Submenu text
@Component({
	standalone: false,
	selector: 'app-sidebar-nav-link',
	changeDetection: ChangeDetectionStrategy.Eager,
	template: `
		@if (!isExternalLink()) {
			<a class="pl-1 nav-link nav-dropdown-toggle" [class.active]="isActive()" (click)="navigateTo($safeNavigationMigration(link?.url))" (click)="hideMobile()">
				@if (isIcon()) {
					<i class="{{ link?.styleclass }}"></i>
				}
				<span>{{ $safeNavigationMigration(link?.pg_title) | translate }}</span>
				@if (hasDescription()) {
					<i class="pi pi-info-circle menu-info-icon" pTooltip="{{ link?.pg_description }}" tooltipPosition="right" (click)="$event.stopPropagation()"></i>
				}
			</a>
		} @else {
			<a (click)="navigateTo($safeNavigationMigration(link?.url))">
				@if (isIcon()) {
					<i class="{{ link?.styleclass }}"></i>
				}
				<span>{{ $safeNavigationMigration(link?.pg_title) | translate }}</span>
				@if (hasDescription()) {
					<i class="pi pi-info-circle menu-info-icon" pTooltip="{{ link?.pg_description }}" tooltipPosition="right" (click)="$event.stopPropagation()"></i>
				}
			</a>
		}
	`,
})
export class AppSidebarNavLinkComponent {
	@Input() link: any;

	public isExternalLink() {
		return this.link?.url && typeof this.link.url === 'string' && this.link.url.substring(0, 4) === 'http';
	}

	public isActive() {
		// pagina di un'altra sezione a schede sotto il suo url: si evidenzia quella sezione
		if ((this.link?.escludiUrls || []).some((u: string) => isMenuUrlActive(this.router, u))) return false;
		// sezione a schede: evidenziata anche sulle pagine delle sue schede
		return isMenuUrlActive(this.router, this.link?.url) || (this.link?.hubUrls || []).some((u: string) => isMenuUrlActive(this.router, u));
	}

	public isIcon() {
		return !!this.link?.styleclass;
	}

	public hasDescription() {
		return !!this.link?.pg_description;
	}

	public hideMobile() {
		if (document.body.classList.contains('sidebar-mobile-show')) {
			document.body.classList.toggle('sidebar-mobile-show');
		}
	}

	constructor(private router: Router) {}
	navigateTo(url: any) {
		if (url != null && url != undefined && url != '' && url != 'null') {
			/*KCS MI - pulizia variabili navigazione*/
			sessionStorage.clear();
			/*KCS MI*/
			// navigateByUrl: gestisce anche url con query string (scorciatoie hub)
			this.router.navigateByUrl(menuRouterUrl(url));
		}
	}
}

@Component({
	standalone: false,
	selector: 'app-sidebar-nav-dropdown',
	changeDetection: ChangeDetectionStrategy.Eager,
	template: `
		@if (minimized) {
			<a class="nav-link nav-dropdown-toggle" [class.active]="isActive()" (mouseenter)="onMouseEnterTrigger($event)" (mouseleave)="onMouseLeaveTrigger()" (click)="navigateTo(link.url, link.pg_id)">
				@if (isIcon()) {
					<i class="{{ link.styleclass }}"></i>
				}
				<span>{{ link.pg_title | translate }}</span>
				@if (hasDescription()) {
					<i class="pi pi-info-circle menu-info-icon" pTooltip="{{ link.pg_description }}" tooltipPosition="right" (click)="$event.stopPropagation()"></i>
				}
			</a>
			<p-popover #op appendTo="body" (onShow)="adjustPosition()" styleClass="menu-popover">
				<div (mouseenter)="onMouseEnterContent()" (mouseleave)="onMouseLeaveContent()" (click)="op.hide()">
					<ul class="nav">
						@for (child of link.submenu; track child) {
							<app-sidebar-nav-item [item]="child" [disableTooltip]="true"></app-sidebar-nav-item>
						}
					</ul>
				</div>
			</p-popover>
		} @else {
			<div class="pre-nav-dropdown-toggle">
				<a class="nav-link nav-dropdown-toggle" [class.active]="isActive()" (click)="navigateTo(link.url, link.pg_id)">
					@if (isIcon()) {
						<i class="{{ link.styleclass }}"></i>
					}
					<span>{{ link.pg_title | translate }}</span>
					@if (hasDescription()) {
						<i class="pi pi-info-circle menu-info-icon" pTooltip="{{ link.pg_description }}" tooltipPosition="right" (click)="$event.stopPropagation()"></i>
					}
				</a>
				@if (hasSubMenu()) {
					<i [ngClass]="'nav-dropdown-toggle-i pi pi-fw pi-angle-' + (submenu ? 'up' : 'down')" (click)="prefToggleDropDown(link.pg_id)"></i>
				}
			</div>

			@if (submenu) {
				<ul class="nav">
					@for (child of link.submenu; track child) {
						<app-sidebar-nav-item [item]="child"></app-sidebar-nav-item>
					}
				</ul>
			}
		}
	`,
})
export class AppSidebarNavDropdownComponent implements OnInit {
	@Input() link: any;
	@ViewChild('op') op!: Popover;
	submenu = false;
	minimized = false;
	private hideTimeout: any;

	constructor(
		private router: Router,
		private sidebarService: SidebarService,
	) {}

	ngOnInit() {
		this.sidebarService.minimized$.subscribe((min) => (this.minimized = min));
		const storedState = localStorage.getItem(`submenu_${this.link.pg_id}`);
		this.submenu = storedState === 'true';
	}

	adjustPosition() {
		const trigger = this.op.target as HTMLElement;
		const popover = this.op.container as HTMLElement;
		const rect = trigger.getBoundingClientRect();
		popover.style.left = `${rect.right + 12}px`;
		popover.style.top = `${rect.top - 20}px`;
		popover.style.maxWidth = `170px`;
	}

	onMouseEnterTrigger($event: any) {
		clearTimeout(this.hideTimeout);
		this.op.show($event);
	}

	onMouseLeaveTrigger() {
		this.hideTimeout = setTimeout(() => this.op.hide(), 200);
	}

	onMouseEnterContent() {
		clearTimeout(this.hideTimeout);
	}

	onMouseLeaveContent() {
		this.op.hide();
	}

	public isActive(): boolean {
		// pagina di un'altra sezione a schede sotto il suo url: si evidenzia quella sezione
		if ((this.link?.escludiUrls || []).some((u: string) => isMenuUrlActive(this.router, u))) {
			return false;
		}
		if (isMenuUrlActive(this.router, this.link?.url)) {
			return true;
		}
		// sezione a schede: evidenziata anche sulle pagine delle sue schede (che non sono più nel sottomenu)
		if ((this.link?.hubUrls || []).some((u: string) => isMenuUrlActive(this.router, u))) {
			return true;
		}
		// Se il dropdown è espanso inline, lascia evidenziare la voce figlia (evita il doppio highlight padre+figlio).
		if (!this.minimized && this.submenu) {
			return false;
		}
		const subs = this.link?.submenu || [];
		return subs.some((c: any) => isMenuUrlActive(this.router, c?.url) || (c?.submenu || []).some((g: any) => isMenuUrlActive(this.router, g?.url)));
	}

	hasSubMenu() {
		return this.link.submenu && this.link.submenu.length > 0;
	}

	public isIcon() {
		return this.link.styleclass ? true : false;
	}

	public hasDescription() {
		return !!this.link?.pg_description;
	}

	navigateTo(url: any, menu_id: number) {
		// menu senza pagina propria: il clic sul nome apre/chiude il sottomenu (prima solo la freccia)
		if (!this.minimized && this.hasSubMenu() && (url == null || url === '' || url === 'null')) {
			this.prefToggleDropDown(menu_id);
			return;
		}
		if (url != null && url != undefined && url != '' && url != 'null') {
			sessionStorage.clear();
			// navigateByUrl: gestisce anche url con query string (scorciatoie hub)
			this.router.navigateByUrl(menuRouterUrl(url));
			if (document.body.classList.contains('sidebar-mobile-show')) {
				document.body.classList.remove('sidebar-mobile-show');
			}
		}
	}

	prefToggleDropDown(menu_id: number) {
		if (this.hasSubMenu()) {
			localStorage.setItem(`submenu_${menu_id}`, this.submenu ? 'false' : 'true');
		}
		this.submenu = !this.submenu;
	}
}

@Component({
	standalone: false,
	selector: 'app-sidebar-nav-title',
	changeDetection: ChangeDetectionStrategy.Eager,
	template: '',
})
export class AppSidebarNavTitleComponent implements OnInit {
	@Input() title: any;

	constructor(
		private el: ElementRef,
		private renderer: Renderer2,
	) {}

	ngOnInit() {
		const nativeElement: HTMLElement = this.el.nativeElement;
		const li = this.renderer.createElement('li');
		const pg_title = this.renderer.createText(this.title.pg_title);

		this.renderer.addClass(li, 'nav-title');

		if (this.title.class) {
			const classes = this.title.class;
			this.renderer.addClass(li, classes);
		}

		if (this.title.wrapper) {
			const wrapper = this.renderer.createElement(this.title.wrapper.element);

			this.renderer.appendChild(wrapper, pg_title);
			this.renderer.appendChild(li, wrapper);
		} else {
			this.renderer.appendChild(li, pg_title);
		}
		this.renderer.appendChild(nativeElement, li);
	}
}

export const APP_SIDEBAR_NAV = [AppSidebarNavComponent, AppSidebarNavDropdownComponent, AppSidebarNavItemComponent, AppSidebarNavLinkComponent, AppSidebarNavTitleComponent];
